import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BASE_URL } from "../../api";
import { getApiHeaders } from "../../utils/helpers";
import ClaimRewardModal from "./ClaimRewardModal";

const FALLBACK_IMAGES = [
  "https://images.unsplash.com/photo-1579586337278-3befd40fd17a?auto=format&fit=crop&w=300&q=80",
  "https://images.unsplash.com/photo-1546868871-7041f2a55e12?auto=format&fit=crop&w=300&q=80",
  "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?auto=format&fit=crop&w=300&q=80",
  "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=300&q=80",
  "https://images.unsplash.com/photo-1563720223185-11003d516935?auto=format&fit=crop&w=300&q=80",
];

const getFallbackImage = (item, idx = 0) => {
  if (item?.image_url) return item.image_url;
  const name = String(item?.reward_name || item?.title || "").toLowerCase();
  if (name.includes("pixel") || (name.includes("smartwatch") && name.includes("google"))) {
    return FALLBACK_IMAGES[0];
  }
  if (name.includes("apple") || name.includes("watch")) {
    return FALLBACK_IMAGES[1];
  }
  if (name.includes("iphone") || name.includes("phone")) {
    return FALLBACK_IMAGES[2];
  }
  if (name.includes("dubai") || name.includes("trip")) {
    return FALLBACK_IMAGES[3];
  }
  if (name.includes("rover") || name.includes("car")) {
    return FALLBACK_IMAGES[4];
  }
  return FALLBACK_IMAGES[idx % FALLBACK_IMAGES.length];
};

export default function RewardsJourney({ rewards: propRewards, onClaimReward, onRefresh }) {
  const [activeTab, setActiveTab] = useState("gallery"); // Default to gallery view
  const [rewardsList, setRewardsList] = useState([]);
  const [loading, setLoading] = useState(!propRewards || propRewards.length === 0);
  const [error, setError] = useState(null);
  const [localClaimReward, setLocalClaimReward] = useState(null);

  const fetchTradeEarnList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${BASE_URL}/deposit/trade_earn_list`, {
        headers: getApiHeaders(),
      });

      if (
        (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) &&
        res.data?.data
      ) {
        const raw = res.data.data;
        const list = Array.isArray(raw) ? raw : [raw];
        setRewardsList(list);
      } else {
        setRewardsList([]);
        setError(res.data?.message || "No rewards found");
      }
    } catch (err) {
      console.error("Error fetching trade_earn_list:", err);
      setRewardsList([]);
      setError(
        err.response?.data?.message ||
        err.response?.data?.msg ||
        "Failed to load rewards"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (propRewards && propRewards.length > 0) {
      setRewardsList(propRewards);
      setLoading(false);
      return;
    }
    fetchTradeEarnList();
  }, [propRewards, fetchTradeEarnList]);

  const handleClaim = (item) => {
    const target = item.rawItem || item;
    if (onClaimReward) {
      onClaimReward(target);
    } else {
      setLocalClaimReward(target);
    }
  };

  const rawItems = propRewards && propRewards.length > 0 ? propRewards : rewardsList;

  const displayRewards = rawItems.map((item, idx) => {
    const rawStatus = String(item.status || "locked").toLowerCase().replace(/[\s_-]/g, "");
    const lotsRequired = Number(item.lots_required ?? item.lots ?? 0);
    const lotsTraded = Number(item.lots_traded ?? item.currentLots ?? 0);
    const progress = Number(
      item.progress_percent ??
      item.progress ??
      (lotsRequired > 0 ? Math.round((lotsTraded / lotsRequired) * 100) : 0)
    );
    const lotsToGo = Number(
      item.lots_to_go ??
      (lotsRequired > 0 ? Math.max(0, lotsRequired - lotsTraded) : 0)
    );

    let normalizedStatus = "locked";
    if (rawStatus === "inprogress" || rawStatus === "active") {
      normalizedStatus = "active";
    } else if (rawStatus === "claimed" || rawStatus === "completed") {
      normalizedStatus = "claimed";
    } else if (rawStatus === "unlocked" || rawStatus === "unlock") {
      normalizedStatus = "unlocked";
    } else if (rawStatus === "pending") {
      normalizedStatus = "pending";
    }

    return {
      id: item.id ?? idx + 1,
      sequenceNo: item.sequence_no ?? idx + 1,
      number: `#${item.sequence_no ?? idx + 1}`,
      title: item.reward_name ?? item.title ?? `Reward #${idx + 1}`,
      shortTitle: item.reward_name ?? item.shortTitle ?? item.title ?? `Reward #${idx + 1}`,
      description: item.description,
      lots: lotsRequired,
      currentLots: lotsTraded,
      progress: progress,
      lotsToGo: lotsToGo,
      rewardValue: Number(item.reward_value ?? item.rewardValue ?? 0),
      status: normalizedStatus,
      rawStatus: item.status,
      image: item.image_url || item.image || getFallbackImage(item, idx),
      claimedDate: item.claimeddate || item.claimedDate || null,
      rawItem: item,
    };
  });

  return (
    <div className="mb-4">
      {/* Top Header & Switcher */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div className="d-flex align-items-center">
          <div
            className="d-flex align-items-center justify-content-center me-2 text-primary"
            style={{ fontSize: "20px" }}
          >
            <i className="bx bx-gift"></i>
          </div>
          <h5
            className="fw-bold mb-0"
            style={{
              fontSize: "clamp(16px, 3.5vw, 18px)",
              color: "var(--bs-emphasis-color)",
            }}
          >
            Rewards Journey
          </h5>
          <span
            className="badge rounded-pill ms-2 px-2 py-1 font-size-11 fw-bold"
            style={{
              backgroundColor: "rgba(var(--bs-primary-rgb), 0.12)",
              color: "var(--bs-primary)",
            }}
          >
            {displayRewards.length}
          </span>
        </div>

        {/* View Switcher: Timeline vs Gallery */}
        <div
          className="d-inline-flex p-1 rounded-3"
          style={{
            backgroundColor: "var(--bs-tertiary-bg)",
            border: "1px solid var(--bs-border-color)",
          }}
        >
          <button
            type="button"
            className={`btn btn-sm d-flex align-items-center gap-1 fw-semibold px-3 py-1 ${activeTab === "timeline"
              ? "btn-light shadow-sm text-primary"
              : "btn-link text-muted text-decoration-none shadow-none"
              }`}
            style={{
              borderRadius: "6px",
              fontSize: "12px",
              transition: "all 0.2s ease",
            }}
            onClick={() => setActiveTab("timeline")}
          >
            <i className="bx bx-list-ul font-size-14"></i>
            <span>Timeline</span>
          </button>

          <button
            type="button"
            className={`btn btn-sm d-flex align-items-center gap-1 fw-semibold px-3 py-1 ${activeTab === "gallery"
              ? "btn-light shadow-sm text-primary"
              : "btn-link text-muted text-decoration-none shadow-none"
              }`}
            style={{
              borderRadius: "6px",
              fontSize: "12px",
              transition: "all 0.2s ease",
            }}
            onClick={() => setActiveTab("gallery")}
          >
            <i className="bx bx-grid-alt font-size-14"></i>
            <span>Gallery</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div
          className="card shadow-sm border-0 py-5 text-center mb-0"
          style={{
            borderRadius: "14px",
            backgroundColor: "var(--bs-secondary-bg)",
          }}
        >
          <div className="card-body">
            <div
              className="spinner-border text-primary mb-3"
              role="status"
              style={{ width: "2.5rem", height: "2.5rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted font-size-13 mb-0">Loading rewards journey...</p>
          </div>
        </div>
      ) : displayRewards.length === 0 ? (
        <div
          className="card shadow-sm border-0 py-5 text-center mb-0"
          style={{
            borderRadius: "14px",
            backgroundColor: "var(--bs-secondary-bg)",
          }}
        >
          <div className="card-body">
            <i className="bx bx-gift font-size-40 text-muted mb-2 d-inline-block"></i>
            <h6 className="fw-semibold mb-1" style={{ color: "var(--bs-emphasis-color)" }}>
              No Rewards Found
            </h6>
            <p className="text-muted font-size-13 mb-0">
              {error || "There are no rewards currently configured in the trade & earn program."}
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Timeline View */}
          {activeTab === "timeline" && (
            <div className="d-flex flex-column gap-2 gap-sm-3">
              {displayRewards.map((item) => {
                const isActive = item.status === "active";
                const isUnlocked = item.status === "unlocked";
                const isClaimed = item.status === "claimed" || item.status === "completed";
                const isPending = item.status === "pending";

                return (
                  <div
                    key={item.id}
                    className={`card mb-0 ${isActive
                      ? "animated-active-card"
                      : isPending
                        ? "shadow-sm border-warning-subtle"
                        : "shadow-sm"
                      }`}
                    style={{
                      borderRadius: "14px",
                      backgroundColor: "var(--bs-secondary-bg)",
                      border: isActive
                        ? "2px solid var(--bs-primary)"
                        : isPending
                          ? "1px solid rgba(var(--bs-warning-rgb), 0.4)"
                          : "1px solid var(--bs-border-color)",
                      overflow: "hidden",
                    }}
                  >
                    <div className="card-body p-3 p-md-3">
                      {/* Top Header Row: #Sequence, Date & Status */}
                      <div className="d-flex align-items-center justify-content-between mb-2 pb-1">
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className={`badge rounded-pill fw-bold font-size-11 px-2.5 py-1 ${isActive
                              ? "bg-primary text-white"
                              : "bg-primary-subtle text-primary"
                              }`}
                          >
                            {item.number}
                          </span>
                          {item.claimedDate && (
                            <span className="font-size-11 text-muted d-inline-flex align-items-center gap-1">
                              <i className="bx bx-calendar font-size-12"></i>
                              {String(item.claimedDate).split("T")[0] || item.claimedDate}
                            </span>
                          )}
                        </div>

                        {/* Status Badge & Claim Action */}
                        <div className="d-flex align-items-center gap-2">
                          {isUnlocked ? (
                            <>
                              <span
                                className="badge rounded-pill fw-semibold font-size-11 px-2.5 py-1.5 d-inline-flex align-items-center gap-1"
                                style={{
                                  backgroundColor: "rgba(var(--bs-primary-rgb), 0.12)",
                                  color: "var(--bs-primary)",
                                  border: "1px solid rgba(var(--bs-primary-rgb), 0.2)",
                                }}
                              >
                                <i className="bx bx-lock-open-alt font-size-11"></i>
                                <span>UNLOCKED</span>
                              </span>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm fw-bold px-3 py-1.5 d-inline-flex align-items-center gap-1.5 shadow-sm"
                                onClick={() => handleClaim(item)}
                                style={{ borderRadius: "8px", fontSize: "12px" }}
                              >
                                <i className="bx bx-gift font-size-15"></i>
                                <span>Claim</span>
                              </button>
                            </>
                          ) : isActive ? (
                            <span
                              className="badge rounded-pill fw-bold font-size-11 px-2.5 py-1 d-inline-flex align-items-center gap-1 shadow-sm"
                              style={{
                                backgroundColor: "rgba(var(--bs-primary-rgb), 0.15)",
                                color: "var(--bs-primary)",
                                border: "1px solid rgba(var(--bs-primary-rgb), 0.25)",
                              }}
                            >
                              <span
                                className="live-dot"
                                style={{ width: "6px", height: "6px" }}
                              ></span>
                              <span>ACTIVE</span>
                            </span>
                          ) : isClaimed ? (
                            <span
                              className="badge rounded-pill fw-bold font-size-11 px-2.5 py-1 d-inline-flex align-items-center gap-1"
                              style={{
                                backgroundColor: "rgba(var(--bs-success-rgb), 0.15)",
                                color: "var(--bs-success)",
                                border: "1px solid rgba(var(--bs-success-rgb), 0.25)",
                              }}
                            >
                              <i className="bx bx-check-circle font-size-12"></i>
                              <span>CLAIMED</span>
                            </span>
                          ) : (
                            <span
                              className="badge rounded-pill fw-semibold font-size-11 px-2.5 py-1 d-inline-flex align-items-center"
                              style={{
                                backgroundColor: "var(--bs-tertiary-bg)",
                                color: "var(--bs-secondary-color)",
                                border: "1px solid var(--bs-border-color)",
                              }}
                            >
                              <i className="bx bx-lock-alt me-1 font-size-11"></i>
                              <span>LOCKED</span>
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Main Product Row: Thumbnail + Info */}
                      <div className="d-flex align-items-center gap-3">
                        {/* Thumbnail */}
                        <div
                          className={`d-flex align-items-center justify-content-center p-1 flex-shrink-0 ${isActive ? "float-animated" : ""
                            }`}
                          style={{
                            width: "56px",
                            height: "56px",
                            borderRadius: "12px",
                            backgroundColor: "rgba(var(--bs-primary-rgb), 0.08)",
                            border: "1px solid var(--bs-border-color)",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={item.image}
                            alt={item.title}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = FALLBACK_IMAGES[0];
                            }}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                          />
                        </div>

                        {/* Title & Lots Info */}
                        <div className="flex-grow-1 min-w-0">
                          <h6
                            className="fw-bold mb-1 text-wrap"
                            style={{
                              fontSize: "14px",
                              color: "var(--bs-emphasis-color)",
                              lineHeight: "1.3",
                            }}
                          >
                            {item.title}
                          </h6>
                          <div className="d-flex align-items-center gap-2 flex-wrap font-size-12">
                            <span className="fw-semibold text-muted text-nowrap">
                              {item.lots} Lots Required
                            </span>
                            {item.rewardValue > 0 && (
                              <>
                                <span className="text-muted font-size-10">•</span>
                                <span className="fw-semibold text-success text-nowrap">
                                  ${Number(item.rewardValue).toLocaleString()} Value
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* ONLY IF ACTIVE: Shimmer Progress Bar & "YOU ARE HERE" indicator */}
                      {isActive && (
                        <div className="mt-3 pt-1">
                          <div className="d-flex align-items-center gap-2 gap-sm-3">
                            <div
                              className="progress flex-grow-1"
                              style={{
                                height: "7px",
                                borderRadius: "10px",
                                backgroundColor: "rgba(var(--bs-primary-rgb), 0.12)",
                              }}
                            >
                              <div
                                className="progress-bar shimmer-bar"
                                role="progressbar"
                                style={{
                                  width: `${Math.min(100, Math.max(0, item.progress))}%`,
                                  borderRadius: "10px",
                                  backgroundImage:
                                    "linear-gradient(90deg, var(--color-info) 0%, var(--bs-primary) 100%)",
                                }}
                                aria-valuenow={item.progress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              ></div>
                            </div>

                            <span className="font-size-11 fw-semibold text-muted text-nowrap">
                              {Number(item.currentLots || 0).toFixed(2)} / {item.lots} Lots
                              {item.lotsToGo > 0 && ` (${item.lotsToGo} to go)`}
                            </span>
                          </div>

                          <div
                            className="text-center mt-2 d-flex align-items-center justify-content-center gap-1 font-size-11 fw-bold text-uppercase"
                            style={{
                              color: "var(--bs-primary)",
                              letterSpacing: "0.8px",
                            }}
                          >
                            <span className="beacon-animated">
                              <i className="bx bxs-navigation font-size-14"></i>
                            </span>
                            <span>YOU ARE HERE</span>
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Gallery View: 2 cols on mobile, 3 on md, 5 on lg */}
          {activeTab === "gallery" && (
            <div className="row row-cols-2 row-cols-sm-2 row-cols-md-3 row-cols-lg-5 g-2 g-md-3">
              {displayRewards.map((item) => {
                const isActive = item.status === "active";
                const isUnlocked = item.status === "unlocked";
                const isClaimed = item.status === "claimed" || item.status === "completed";
                const isPending = item.status === "pending";

                return (
                  <div key={item.id} className="col">
                    <div
                      className={`card h-100 mb-0 text-center position-relative ${isActive
                        ? "animated-active-card"
                        : isPending
                          ? "shadow-sm border-warning-subtle"
                          : "shadow-sm"
                        }`}
                      style={{
                        borderRadius: "14px",
                        backgroundColor: "var(--bs-secondary-bg)",
                        border: isActive
                          ? "2px solid var(--bs-primary)"
                          : isPending
                            ? "1px solid rgba(var(--bs-warning-rgb), 0.4)"
                            : "1px solid var(--bs-border-color)",
                        overflow: "hidden",
                      }}
                    >
                      <div className="card-body p-2 p-sm-3 d-flex flex-column">
                        <div
                          className="d-flex align-items-center justify-content-between mb-2 mb-sm-3 w-100"
                          style={{ minHeight: "24px" }}
                        >
                          <div>
                            {isActive ? (
                              <span
                                className="badge rounded-pill fw-bold font-size-10 font-size-sm-11 px-2 py-0.5 text-white shadow-sm"
                                style={{
                                  backgroundColor: "var(--bs-primary)",
                                }}
                              >
                                {item.number}
                              </span>
                            ) : (
                              <span className="font-size-10 font-size-sm-11 text-muted fw-semibold">
                                {item.number}
                              </span>
                            )}
                          </div>

                          <div>
                            {isPending ? (
                              <span
                                className="badge rounded-pill fw-semibold font-size-10 font-size-sm-11 px-1.5 px-sm-2 py-0.5 d-inline-flex align-items-center gap-1 shadow-sm"
                                style={{
                                  backgroundColor: "rgba(var(--bs-warning-rgb), 0.15)",
                                  color: "#e2a03f",
                                  border: "1px solid rgba(var(--bs-warning-rgb), 0.3)",
                                }}
                              >
                                <i className="bx bx-time-five font-size-10"></i>
                                <span>Awaiting approval</span>
                              </span>
                            ) : isActive ? (
                              <span
                                className="badge rounded-pill fw-bold font-size-10 font-size-sm-11 px-1.5 px-sm-2 py-0.5 d-inline-flex align-items-center gap-1"
                                style={{
                                  backgroundColor: "rgba(var(--bs-primary-rgb), 0.15)",
                                  color: "var(--bs-primary)",
                                }}
                              >
                                <span
                                  className="live-dot"
                                  style={{ width: "5px", height: "5px" }}
                                ></span>
                                <span>ACTIVE</span>
                              </span>
                            ) : isUnlocked ? (
                              <span
                                className="badge rounded-pill fw-semibold font-size-10 font-size-sm-11 px-1.5 px-sm-2 py-0.5 d-inline-flex align-items-center gap-1"
                                style={{
                                  backgroundColor: "rgba(var(--bs-primary-rgb), 0.12)",
                                  color: "var(--bs-primary)",
                                  border: "1px solid rgba(var(--bs-primary-rgb), 0.2)",
                                }}
                              >
                                <i className="bx bx-lock-open-alt font-size-10"></i>
                                <span>UNLOCKED</span>
                              </span>
                            ) : isClaimed ? (
                              <span
                                className="badge rounded-pill fw-bold font-size-10 font-size-sm-11 px-1.5 px-sm-2 py-0.5 d-inline-flex align-items-center gap-1"
                                style={{
                                  backgroundColor: "rgba(var(--bs-success-rgb), 0.15)",
                                  color: "var(--bs-success)",
                                }}
                              >
                                <i className="bx bx-check-circle me-0.5 font-size-10"></i>
                                <span>CLAIMED</span>
                              </span>
                            ) : (
                              <span
                                className="badge rounded-pill fw-semibold font-size-10 font-size-sm-11 px-1.5 px-sm-2 py-0.5"
                                style={{
                                  backgroundColor: "var(--bs-tertiary-bg)",
                                  color: "var(--bs-secondary-color)",
                                  border: "1px solid var(--bs-border-color)",
                                }}
                              >
                                <i className="bx bx-lock-alt me-0.5 font-size-10"></i>
                                LOCKED
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-center mb-2 mb-sm-3">
                          <div
                            className={`reward-card-thumb d-flex align-items-center justify-content-center p-1 rounded-3 ${isActive ? "float-animated" : ""
                              }`}
                            style={{
                              backgroundColor: "rgba(var(--bs-primary-rgb), 0.08)",
                              border: "1px solid var(--bs-border-color)",
                              overflow: "hidden",
                            }}
                          >
                            <img
                              src={item.image}
                              alt={item.title}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = FALLBACK_IMAGES[0];
                              }}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "8px",
                              }}
                            />
                          </div>
                        </div>

                        <div>
                          <h6
                            className="fw-bold mb-1 text-truncate-2"
                            style={{
                              fontSize: "12px",
                              height: "32px",
                              color: "var(--bs-emphasis-color)",
                              lineHeight: "1.3",
                            }}
                            title={item.title}
                          >
                            {item.shortTitle || item.title}
                          </h6>
                          <div
                            className="font-size-11 font-size-sm-12 fw-medium"
                            style={{ color: "var(--bs-secondary-color)" }}
                          >
                            {item.lots} Lots
                          </div>
                        </div>

                        {/* ONLY IF ACTIVE: Shimmer progress bar and YOU ARE HERE */}
                        {isActive && (
                          <div className="mt-auto pt-2 pt-sm-3">
                            <div
                              className="progress mb-1 mb-sm-2"
                              style={{
                                height: "5px",
                                borderRadius: "10px",
                                backgroundColor: "rgba(var(--bs-primary-rgb), 0.12)",
                              }}
                            >
                              <div
                                className="progress-bar shimmer-bar"
                                role="progressbar"
                                style={{
                                  width: `${Math.min(100, Math.max(0, item.progress))}%`,
                                  borderRadius: "10px",
                                  backgroundImage:
                                    "linear-gradient(90deg, var(--color-info) 0%, var(--bs-primary) 100%)",
                                }}
                                aria-valuenow={item.progress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              ></div>
                            </div>

                            <div
                              className="font-size-9 font-size-sm-10 fw-bold text-uppercase d-flex align-items-center justify-content-center gap-1"
                              style={{
                                color: "var(--bs-primary)",
                                letterSpacing: "0.5px",
                              }}
                            >
                              <span className="beacon-animated">
                                <i className="bx bxs-navigation font-size-11"></i>
                              </span>
                              <span>YOU ARE HERE</span>
                            </div>
                          </div>
                        )}

                        {/* IF UNLOCKED: Claim Button */}
                        {isUnlocked && (
                          <div className="mt-auto pt-2 pt-sm-3">
                            <button
                              type="button"
                              className="btn btn-sm btn-primary w-100 fw-bold py-1 px-1 d-flex align-items-center justify-content-center gap-1 shadow-sm"
                              onClick={() => handleClaim(item)}
                              style={{ borderRadius: "8px", fontSize: "11px" }}
                            >
                              <i className="bx bx-gift font-size-13"></i>
                              <span>Claim</span>
                            </button>
                          </div>
                        )}

                        {/* IF CLAIMED */}
                        {isClaimed && (
                          <div className="mt-auto pt-2 pt-sm-3">
                            <span
                              className="badge rounded-pill font-size-10 font-size-sm-11 fw-semibold w-100 py-1 d-inline-flex align-items-center justify-content-center gap-1"
                              style={{
                                backgroundColor: "rgba(var(--bs-success-rgb), 0.12)",
                                color: "var(--bs-success)",
                                border: "1px solid rgba(var(--bs-success-rgb), 0.25)",
                              }}
                            >
                              <i className="bx bx-check-circle font-size-12"></i>
                              <span>Claimed</span>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Fallback local claim modal if parent didn't pass onClaimReward */}
      {!onClaimReward && (
        <ClaimRewardModal
          isOpen={Boolean(localClaimReward)}
          reward={localClaimReward}
          onClose={() => setLocalClaimReward(null)}
          onSuccess={() => {
            if (onRefresh) onRefresh();
            else fetchTradeEarnList();
          }}
        />
      )}

      <style>{`
        .live-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: var(--bs-primary);
          display: inline-block;
          animation: pulseDot 1.8s infinite ease-in-out;
        }
        @keyframes pulseDot {
          0% {
            transform: scale(0.9);
            box-shadow: 0 0 0 0 rgba(var(--bs-primary-rgb), 0.7);
          }
          70% {
            transform: scale(1.1);
            box-shadow: 0 0 0 6px rgba(var(--bs-primary-rgb), 0);
          }
          100% {
            transform: scale(0.9);
            box-shadow: 0 0 0 0 rgba(var(--bs-primary-rgb), 0);
          }
        }
        .animated-active-card {
          box-shadow: 0 8px 24px -4px rgba(var(--bs-primary-rgb), 0.25) !important;
          animation: glowBorder 3s infinite alternate ease-in-out;
        }
        @keyframes glowBorder {
          from {
            border-color: rgba(var(--bs-primary-rgb), 0.6);
          }
          to {
            border-color: var(--bs-primary);
          }
        }
        .shimmer-bar {
          position: relative;
          overflow: hidden;
        }
        .shimmer-bar::after {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.45),
            transparent
          );
          animation: shimmerSweep 2.2s infinite;
        }
        @keyframes shimmerSweep {
          100% {
            left: 100%;
          }
        }
        .beacon-animated {
          display: inline-block;
          animation: bounceBeacon 1.5s infinite ease-in-out;
        }
        @keyframes bounceBeacon {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }
        .float-animated {
          animation: floatThumb 3s infinite ease-in-out;
        }
        @keyframes floatThumb {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-2px);
          }
        }
        .text-truncate-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .reward-card-thumb {
          width: 62px;
          height: 62px;
        }
        @media (min-width: 576px) {
          .reward-card-thumb {
            width: 76px;
            height: 76px;
          }
        }
        @media (max-width: 576px) {
          .font-size-9 {
            font-size: 9px !important;
          }
          .font-size-10 {
            font-size: 10px !important;
          }
        }
      `}</style>
    </div>
  );
}
